<template>
  <v-container fluid>
    <div class="d-flex flex-row flex-wrap align-center ml-2">
      <PageTitle text="Cards Management" class="font-weight-bold ft" />

      <v-btn
        v-ripple="{ class: 'primary--text' }"
        color="primary"
        class="ml-2 mt-2 white--text ft font-weight-medium text-capitalize"
        style="font-size: 12px"
        @click="openCreateDialog"
      >
        New Category <v-icon size="20" color="white">mdi-plus</v-icon>
      </v-btn>

      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            small
            color="primary"
            class="mt-2 ml-2"
            :loading="listLoading"
            @click="refreshPage"
            v-on="on"
          >
            <i class="material-icons-outlined">refresh</i>
          </v-btn>
        </template>
        <span class="ft font-weight-medium font-size-sm">Refresh Page</span>
      </v-tooltip>

      <v-text-field
        v-model="search"
        dense
        append-icon="search"
        light
        single-line
        solo-inverted
        placeholder="Search card categories ..."
        class="mt-2 ml-4 ft font-size-sm black--text cards-search"
        hide-details
        flat
      />
    </div>

    <div class="ml-2 mt-4">
      <h4 class="blue-grey--text ft font-size-md text-uppercase">
        List Of Card Categories
      </h4>
    </div>

    <v-row
      align="center"
      justify="center"
      style="margin-top: 5px; margin-left:1px; margin-right:10px"
    >
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="categories"
          :search="search"
          :loading="listLoading"
          item-key="id"
          loading-text="Loading... Please wait"
          class="box-shadow-light ft font-weight-medium font-size-md"
          :footer-props="{
            itemsPerPageOptions: [10, 20, 30]
          }"
        >
          <template #item.name="{ item }">
            <span class="ft font-weight-bold">{{ item.name }}</span>
          </template>

          <template #item.status="{ item }">
            <v-chip small label :color="item.status ? '#9efb9e66' : '#f98c6b'">
              <span
                class="ft font-weight-bold text-uppercase"
                :style="{
                  color: item.status ? 'rgba(3, 91, 3, 0.83)' : '#862c00'
                }"
              >
                {{ item.status ? "Active" : "Inactive" }}
              </span>
            </v-chip>
          </template>

          <template #item.createdAt="{ item }">
            <span class="ft font-weight-medium">
              {{ item.createdAt | timestamp("DD/MM/YYYY") }}
            </span>
          </template>

          <template #item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  small
                  color="primary"
                  @click="openEditDialog(item)"
                  v-on="on"
                >
                  <v-icon style="font-size: 16px;">mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span class="ft font-weight-medium font-size-sm">
                Edit Card Category
              </span>
            </v-tooltip>
          </template>

          <template #no-data>
            <EmptyPage
              title="No card categories found"
              subTitle="Added card categories will show up here."
              :image="require('@/assets/folder.svg')"
              imageWidth="120px"
            />
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog
      v-model="dialog"
      max-width="460"
      overlay-color="rgba(0,0,0,0.4)"
      overlay-opacity="0.8"
      persistent
    >
      <v-form ref="categoryForm" v-model="formValid" @submit.prevent="save">
        <v-card flat>
          <v-card-title>
            <span class="ft font-size-lg font-weight-bold blue-grey--text ml-1">
              {{ dialogTitle }}
            </span>
            <v-spacer />
          </v-card-title>

          <v-card-text>
            <v-text-field
              v-model.trim="form.name"
              outlined
              autofocus
              label="Category Name"
              class="ft font-weight-medium font-size-sm"
              :rules="[rules.required]"
            />

            <v-switch
              v-model="form.status"
              inset
              color="primary"
              class="ft font-weight-medium"
              label="Active"
            />
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn text class="ft text-capitalize" @click="closeDialog">
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              type="submit"
              class="ft white--text text-capitalize"
              :loading="loading"
              :disabled="loading"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <SnackBar
      :showSnackBar="showSnackBar"
      :message="message"
      :status="status"
    />
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import SnackBarMixins from "../../../mixins/SnackBarMixins";
import { timestamp } from "../../../filters/timestamp";

const defaultForm = () => ({
  id: null,
  name: "",
  status: true
});

export default {
  name: "CardsIndex",
  components: {
    PageTitle: () => import("../../../components/PageTitle"),
    EmptyPage: () => import("../../../components/EmptyPage"),
    SnackBar: () => import("../../../components/SnackBar")
  },
  mixins: [SnackBarMixins],
  data() {
    return {
      dialog: false,
      form: defaultForm(),
      formValid: true,
      search: "",
      rules: {
        required: value => !!value || "Field is required"
      }
    };
  },
  filters: {
    timestamp
  },
  computed: {
    ...mapGetters({
      categories: "cards/getCardCategories",
      listLoading: "getIsListLoading",
      loading: "getIsLoading"
    }),
    headers() {
      return [
        {
          text: "Name",
          value: "name"
        },
        {
          text: "Slug",
          value: "slug"
        },
        {
          text: "Status",
          value: "status"
        },
        {
          text: "Created At",
          value: "createdAt"
        },
        {
          text: "Actions",
          value: "actions",
          align: "center",
          sortable: false
        }
      ];
    },
    isEditing() {
      return Boolean(this.form.id);
    },
    dialogTitle() {
      return this.isEditing ? "Edit Card Category" : "Add Card Category";
    }
  },
  methods: {
    refreshPage() {
      this.$store.dispatch("cards/listCategories");
    },
    openCreateDialog() {
      this.form = defaultForm();
      this.dialog = true;
      this.$nextTick(() => {
        this.$refs.categoryForm?.resetValidation();
      });
    },
    openEditDialog(category) {
      this.form = {
        id: category.id,
        name: category.name,
        status: Boolean(category.status)
      };
      this.dialog = true;
      this.$nextTick(() => {
        this.$refs.categoryForm?.resetValidation();
      });
    },
    closeDialog() {
      this.dialog = false;
      this.form = defaultForm();
    },
    async save() {
      if (!this.$refs.categoryForm.validate()) {
        return;
      }

      const payload = {
        name: this.form.name,
        status: this.form.status
      };

      if (this.isEditing) {
        await this.$store.dispatch("cards/updateCategory", {
          id: this.form.id,
          ...payload
        });
      } else {
        await this.$store.dispatch("cards/createCategory", payload);
      }

      this.closeDialog();
    }
  },
  created() {
    this.refreshPage();
  }
};
</script>

<style scoped>
.cards-search {
  max-width: 500px;
  min-width: 260px;
}
</style>
